import { TextInput, type UseFormReturn } from '@faceup/form'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Form } from '@faceup/ui'
import { Checkbox } from '@faceup/ui-base'

const messages = defineMessages('followUp.pages.createReport.components.aFSenderNameField', [
  { id: 'senderIdentificationLabel', defaultMessage: '' },
  { id: 'sendAnonymously', defaultMessage: '', wrappers: { bold: true } },
  { id: 'anonymousSelected', defaultMessage: '' },
  { id: 'nonAnonymousSelected', defaultMessage: '' },
] as const)

type AF_SenderNameFieldProps = {
  form: UseFormReturn
  senderNameFieldId: string
  senderNameLabel: string
  senderNameHint?: string
  isAnonymous: boolean
  setIsAnonymous: (value: boolean) => void
  isAnonymityEnabled: boolean
  isSchoolDefaultForm: boolean
}

export const AF_SenderNameField = ({
  form,
  senderNameFieldId,
  senderNameLabel,
  senderNameHint,
  isAnonymous,
  setIsAnonymous,
  isAnonymityEnabled,
  isSchoolDefaultForm,
}: AF_SenderNameFieldProps) => {
  const { formatMessage } = useIntl()

  const handleAnonymousChange = (checked: boolean) => {
    setIsAnonymous(checked)
    if (checked) {
      form.setValue(`${senderNameFieldId}.value`, '')
    }
  }

  // No anonymity UI for school forms
  if (isSchoolDefaultForm) {
    return null
  }

  // Card with sender identification section
  return (
    <fieldset
      className='rounded-t-[20px] rounded-b-none bg-colorFillTertiary px-6 pb-4 pt-8 flex flex-col gap-4 border-none'
      aria-label={formatMessage(messages.senderIdentificationLabel)}
    >
      {isAnonymityEnabled && (
        <Form.Item>
          <Checkbox
            onChange={e => handleAnonymousChange(e.target.checked)}
            checked={isAnonymous}
            data-test='create-company-report-form-anonymously-checkbox'
          >
            <FormattedMessage
              message={messages.sendAnonymously}
              wrappers={{ bold: chunks => <strong key='bold'>{chunks}</strong> }}
            />
          </Checkbox>
        </Form.Item>
      )}

      {!isAnonymous && (
        <TextInput
          control={form.control}
          name={`${senderNameFieldId}.value`}
          label={senderNameLabel}
          hint={senderNameHint}
          withAsterisk
          autoComplete='name'
          data-test='create-company-report-form-sender-name-input'
          formItemProps={{
            'data-test': 'create-company-report-form-sender-name',
          }}
        />
      )}

      {isAnonymityEnabled && (
        <span className='sr-only' aria-live='polite' aria-atomic='true'>
          {isAnonymous
            ? formatMessage(messages.anonymousSelected)
            : formatMessage(messages.nonAnonymousSelected)}
        </span>
      )}
    </fieldset>
  )
}
